import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Lightbulb, Compass, AlertCircle, Share2 } from 'lucide-react';
import { SharePopover, buildSnippet } from './ShareInsight';

const TYPE_CONFIG = {
  'decision-insight': {
    label: 'DECISION INSIGHT',
    snippetTag: 'MODELED',
    icon: Lightbulb,
    accentColor: 'border-l-primary',
    badgeBg: 'bg-primary/10',
    badgeText: 'text-primary',
    badgeBorder: 'border-primary/30',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  'strategic-interpretation': {
    label: 'STRATEGIC INTERPRETATION',
    snippetTag: 'DIRECTIONAL',
    icon: Compass,
    accentColor: 'border-l-stable',
    badgeBg: 'bg-stable/10',
    badgeText: 'text-stable',
    badgeBorder: 'border-stable/30',
    iconBg: 'bg-stable/10',
    iconColor: 'text-stable',
  },
};

function KeyPointItem({ point, dotClass }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      <span className="text-foreground/80">{point}</span>
    </div>
  );
}

export const DecisionInsightPanel = ({ type = 'decision-insight', title, narrative, keyPoints = [], disclaimer }) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG['decision-insight'];
  const Icon = config.icon;
  const [showShare, setShowShare] = useState(false);
  const dotClass = config.iconBg.replace('/10', '/40');

  const snippet = showShare
    ? buildSnippet({ snippetTag: config.snippetTag, label: config.label, title, narrative, keyPoints, disclaimer })
    : '';

  return (
    <Card className={`bg-card border-border/50 border-l-4 ${config.accentColor}`} data-testid={`${type}-panel`}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className={`p-2 rounded-lg ${config.iconBg} flex-shrink-0 mt-0.5`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Badge className={`${config.badgeBg} ${config.badgeText} ${config.badgeBorder} border text-[10px] font-semibold tracking-wider`}>
                  {config.label}
                </Badge>
                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30 border text-[10px]">
                  DIRECTIONAL
                </Badge>
              </div>
              <Button
                variant="ghost" size="sm"
                className="h-7 px-2 text-2xs gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => setShowShare(s => !s)}
                data-testid="share-insight-btn"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </Button>
            </div>

            {title && <h3 className="text-base font-semibold text-foreground mb-2" data-testid={`${type}-title`}>{title}</h3>}

            <p className="text-sm text-muted-foreground leading-relaxed mb-4" data-testid={`${type}-narrative`}>{narrative}</p>

            {keyPoints.length > 0 && (
              <div className="space-y-2 mb-4" data-testid={`${type}-key-points`}>
                {keyPoints.map((point, i) => <KeyPointItem key={i} point={point} dotClass={dotClass} />)}
              </div>
            )}

            {disclaimer && (
              <div className="flex items-start gap-2 pt-3 border-t border-border/30">
                <AlertCircle className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">{disclaimer}</p>
              </div>
            )}

            {showShare && <SharePopover snippet={snippet} onClose={() => setShowShare(false)} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DecisionInsightPanel;
